import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { QuestService } from '../../../services/quest.service';

interface Session {
  id: number;
  heading: string;
  description: string;
  image: string;
  audio: string;
  audioUrl?: string | null; // URL from backend for edit mode
  backendId?: string; // original session id from backend (for updates/deletes)
  imageFile?: File | null;
  audioFile?: File | null;
  tasks: string[];
  isExpanded: boolean;
}

@Component({
  selector: 'app-create-quest',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './create-quest.component.html',
  styleUrl: './create-quest.component.css'
})
export class CreateQuestComponent implements OnInit {
  @Output() questCreated = new EventEmitter<void>();
  @Output() backClicked = new EventEmitter<void>();
  @Output() formOpened = new EventEmitter<void>();

  showForm: boolean = false;
  
  // Dashboard Card Section
  questName: string = '';
  questDescription: string = '';
  questImagePreview: string = '';
  questImageFile: File | null = null;

  // Quest Journey
  introductionExpanded: boolean = false;
  introductionDescription: string = '';
  introductionImagePreview: string = '';
  introductionImageFile: File | null = null;
  introductionAudioFile: File | null = null;
  introductionAudioName: string = '';
  introductionAudioUrl: string | null = null; // For edit mode - stores URL from backend

  sessions: Session[] = [];
  sessionCounter: number = 0;

  // Image constraints
  private readonly questImageMaxWidth = 100;
  private readonly questImageMaxHeight = 108;
  private readonly introImageWidthReq = 100;
  private readonly introImageHeightReq = 94;
  allQuests: any[] = [];
  recentQuests: any[] = [];
  showAllQuests: boolean = false;
  questSearchQuery: string = '';

  // Validation modal
  showValidationModal = false;
  validationErrors: string[] = [];

  // Success modal
  showSuccessModal = false;

  // Saving loader
  isSaving = false;

  // Edit mode
  isEditMode = false;
  questId: string | null = null;
  originalQuestData: any = null; // Store original data to track changes
  publishingQuestId: string | null = null; // Track quest being (un)published
  deletedSessionIds: string[] = []; // Track removed session IDs for update
  
  // Confirmation modal state
  showPublishConfirmModal = false;
  questToPublish: any = null;
  isPublishing: boolean = false; // true for "Publish to All", false for "Back to Draft"
  
  // Preview modal state
  showPreviewModal = false;
  playingAudios: { [key: string]: boolean } = {};
  audioElements: { [key: string]: HTMLAudioElement } = {};
  audioElementUrls: { [key: string]: string } = {}; // Store the URL used to create each audio element
  audioBlobUrls: { [key: string]: string } = {}; // Store blob URLs for file objects to reuse them

  constructor(private questService: QuestService) {}

  ngOnInit(): void {
    this.loadQuests();
  }

  private loadQuests(): void {
    this.questService.getQuests().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        this.allQuests = [...list].sort((a: any, b: any) => {
          const da = new Date(a.createdDateTime || a.createdDate || 0).getTime();
          const db = new Date(b.createdDateTime || b.createdDate || 0).getTime();
          return db - da;
        });
        this.recentQuests = this.allQuests.slice(0, 5);
      },
      error: (err) => {
        console.error('Error loading quests list', err);
      },
    });
  }

  onCreateQuestClick(): void {
    this.isEditMode = false;
    this.questId = null;
    this.showForm = true;
    this.formOpened.emit();
    // Initialize with Session 1 when form is first shown
    if (this.sessions.length === 0) {
      this.addSession();
    }
  }

  onEditQuestClick(questId: string): void {
    this.isEditMode = true;
    this.questId = questId;
    this.loadQuestForEdit(questId);
  }

  // Get filtered quests based on search query
  get filteredQuests(): any[] {
    const questsToFilter = this.showAllQuests ? this.allQuests : this.recentQuests;
    if (!this.questSearchQuery || this.questSearchQuery.trim() === '') {
      return questsToFilter;
    }
    const query = this.questSearchQuery.toLowerCase().trim();
    return questsToFilter.filter((quest: any) => 
      (quest.name || '').toLowerCase().includes(query)
    );
  }

  // Helpers for state handling
  isQuestPublished(quest: any): boolean {
    const state = (quest?.state || quest?.status || quest?.State || quest?.Status || '').toLowerCase();
    return state === 'published';
  }

  onToggleQuestPublish(quest: any): void {
    const questId = quest?.id || quest?._id;
    if (!questId) {
      return;
    }
    // Show confirmation modal
    this.questToPublish = quest;
    this.isPublishing = !this.isQuestPublished(quest); // true if currently draft (will publish), false if currently published (will draft)
    this.showPublishConfirmModal = true;
  }

  confirmPublishQuest(): void {
    if (!this.questToPublish) {
      return;
    }
    const questId = this.questToPublish?.id || this.questToPublish?._id;
    if (!questId) {
      return;
    }
    const nextState = this.isPublishing ? 'Published' : 'Draft';
    const formData = new FormData();
    formData.append('State', nextState);

    this.publishingQuestId = questId;
    this.questService.updateQuest(questId, formData).subscribe({
      next: () => {
        this.publishingQuestId = null;
        this.closePublishConfirmModal();
        this.loadQuests();
      },
      error: (err) => {
        console.error('Error updating quest state', err);
        this.publishingQuestId = null;
        alert('Failed to update quest state. Please try again.');
      },
    });
  }

  closePublishConfirmModal(): void {
    this.showPublishConfirmModal = false;
    this.questToPublish = null;
    this.isPublishing = false;
  }

  openPreviewModal(): void {
    // Stop all playing audios before opening
    this.stopAllAudios();
    // Clear all cached audio elements so they get recreated with latest URLs
    this.audioElements = {};
    this.audioElementUrls = {};
    this.playingAudios = {};
    // Revoke old blob URLs and clear them
    Object.keys(this.audioBlobUrls).forEach(key => {
      if (this.audioBlobUrls[key]) {
        URL.revokeObjectURL(this.audioBlobUrls[key]);
      }
    });
    this.audioBlobUrls = {};
    this.showPreviewModal = true;
  }

  closePreviewModal(): void {
    this.stopAllAudios();
    this.showPreviewModal = false;
  }

  stopAllAudios(): void {
    Object.keys(this.audioElements).forEach(key => {
      if (this.audioElements[key]) {
        this.audioElements[key].pause();
        this.audioElements[key].currentTime = 0;
      }
    });
    this.playingAudios = {};
  }

  getAudioUrl(audioFile: File | null | undefined, audioUrl: string | null, audioName: string): string | null {
    // If there's a new file, create or reuse blob URL
    if (audioFile) {
      // Check if we already have a blob URL for this file
      if (!this.audioBlobUrls[audioName]) {
        this.audioBlobUrls[audioName] = URL.createObjectURL(audioFile);
      }
      return this.audioBlobUrls[audioName];
    }
    // Otherwise, use the URL (for edit mode with existing audio)
    if (audioUrl) {
      return audioUrl;
    }
    return null;
  }

  toggleAudio(audioUrl: string | null, id: string): void {
    if (!audioUrl) {
      return;
    }

    // Check if we need to create a new audio element or update existing one
    let audio = this.audioElements[id];
    const storedUrl = this.audioElementUrls[id];
    
    // If no audio element exists, or if the URL has changed, create/update it
    if (!audio || storedUrl !== audioUrl) {
      // If old audio exists, clean it up
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        // Revoke old blob URL if it was a blob
        if (storedUrl && storedUrl.startsWith('blob:')) {
          URL.revokeObjectURL(storedUrl);
        }
      }
      
      // Create new audio element with the new URL
      audio = new Audio(audioUrl);
      this.audioElements[id] = audio;
      this.audioElementUrls[id] = audioUrl; // Store the URL we used
      
      audio.addEventListener('ended', () => {
        this.playingAudios[id] = false;
      });
    } else {
      // Use existing audio element
      audio = this.audioElements[id];
    }

    const isCurrentlyPlaying = !audio.paused && !audio.ended;

    if (isCurrentlyPlaying) {
      // Pause
      audio.pause();
      this.playingAudios[id] = false;
    } else {
      // Pause all other audios
      Object.keys(this.audioElements).forEach(key => {
        if (key !== id && this.audioElements[key] && !this.audioElements[key].paused) {
          this.audioElements[key].pause();
          this.playingAudios[key] = false;
        }
      });

      // Reset if ended
      if (audio.ended) {
        audio.currentTime = 0;
      }

      // Play
      audio.play().then(() => {
        this.playingAudios[id] = true;
      }).catch((error) => {
        console.error('Error playing audio:', error);
        this.playingAudios[id] = false;
      });
    }
  }

  isAudioPlaying(id: string): boolean {
    return this.playingAudios[id] === true;
  }

  // Helper methods to get preview URLs (prioritize new files over existing URLs)
  getPreviewImageUrl(imageFile: File | null, existingUrl: string | null): string | null {
    if (imageFile) {
      // If new file uploaded, create object URL
      return URL.createObjectURL(imageFile);
    }
    // Otherwise, use existing URL from backend
    return existingUrl || null;
  }

  getSessionPreviewImage(session: Session): string | null {
    if (session.imageFile) {
      return URL.createObjectURL(session.imageFile);
    }
    return session.image || null;
  }

  getSessionAudioUrl(session: Session): string | null {
    // If new audio file uploaded, create or reuse blob URL
    if (session.audioFile) {
      const audioId = 'session-' + session.id;
      // Check if we already have a blob URL for this file
      if (!this.audioBlobUrls[audioId]) {
        this.audioBlobUrls[audioId] = URL.createObjectURL(session.audioFile);
      }
      return this.audioBlobUrls[audioId];
    }
    // Otherwise, use existing URL from backend
    return session.audioUrl || null;
  }

  loadQuestForEdit(questId: string): void {
    this.questService.getQuests().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        // Find the quest by ID
        const quest = list.find((q: any) => (q.id === questId || q._id === questId));
        if (!quest) {
          alert('Quest not found');
          return;
        }

        // Store original quest data for change tracking
        this.originalQuestData = JSON.parse(JSON.stringify(quest));

        // Reset form first
        this.resetForm(true);
        this.isEditMode = true;
        this.questId = questId;

        // Dashboard Card Section
        this.questName = quest.name || '';
        this.questDescription = quest.shortDescription || '';
        if (quest.thumbnail) {
          this.questImagePreview = quest.thumbnail;
          // Note: We can't convert base64 URL to File, so imageFile will be null
          // User will need to re-upload if they want to change it
        }

        // Introduction Section
        if (quest.introduction) {
          this.introductionDescription = quest.introduction.description || '';
          if (quest.introduction.thumbnail) {
            this.introductionImagePreview = quest.introduction.thumbnail;
          }
          if (quest.introduction.media) {
            this.introductionAudioName = 'Audio file loaded';
            this.introductionAudioUrl = quest.introduction.media; // Store URL for preview
            // Note: We can't convert base64 URL to File, so audioFile will be null
            // User will need to re-upload if they want to change it
          }
          this.introductionExpanded = true;
        }

        // Sessions
        if (quest.sessions && Array.isArray(quest.sessions)) {
          this.sessions = quest.sessions.map((session: any, index: number) => {
            const tasks: string[] = [];
            if (session.tasks && Array.isArray(session.tasks)) {
              session.tasks.forEach((task: any) => {
                if (task.activities && Array.isArray(task.activities)) {
                  tasks.push(...task.activities);
                } else if (typeof task === 'string') {
                  tasks.push(task);
                }
              });
            }
            if (tasks.length === 0) {
              tasks.push('');
            }

            return {
              id: index + 1,
              heading: session.shortDescription || '',
              description: session.longDescription || '',
              image: session.thumbnail || '',
              audio: session.media || '', // Store the actual URL, not just a string
              audioUrl: session.media || null, // Store URL separately for preview
              backendId: session.id || session._id, // preserve backend session id
              imageFile: null, // Will need to re-upload if changed
              audioFile: null, // Will need to re-upload if changed
              tasks: tasks,
              isExpanded: false
            };
          });
          this.sessionCounter = this.sessions.length;
        } else {
          // If no sessions, add one empty session
          this.addSession();
        }

        this.showForm = true;
        this.formOpened.emit();
      },
      error: (err) => {
        console.error('Error loading quest for edit', err);
        alert('Failed to load quest. Please try again.');
      },
    });
  }

  onBackClick(): void {
    this.showForm = false;
    this.isEditMode = false;
    this.questId = null;
    this.originalQuestData = null;
    this.backClicked.emit();
    this.resetForm();
  }

  toggleIntroduction(): void {
    this.introductionExpanded = !this.introductionExpanded;
  }

  toggleSession(sessionId: number): void {
    const session = this.sessions.find(s => s.id === sessionId);
    if (session) {
      session.isExpanded = !session.isExpanded;
    }
  }

  addSession(): void {
    this.sessionCounter++;
    this.sessions.push({
      id: this.sessionCounter,
      heading: '',
      description: '',
      image: '',
      audio: '',
      tasks: [''],
      isExpanded: false
    });
  }

  removeSession(sessionId: number): void {
    const index = this.sessions.findIndex(s => s.id === sessionId);
    if (index !== -1 && this.sessions.length > 1) {
      const session = this.sessions[index];
      // If editing an existing quest and this session exists in backend, track it for deletion
      if (this.isEditMode && session.backendId) {
        const backendIdStr = String(session.backendId);
        if (!this.deletedSessionIds.includes(backendIdStr)) {
          this.deletedSessionIds.push(backendIdStr);
        }
      }

      this.sessions.splice(index, 1);
      // Renumber remaining sessions so labels stay 1,2,3...
      this.sessions.forEach((s, idx) => {
        s.id = idx + 1;
      });
      this.sessionCounter = this.sessions.length;
    }
  }

  addTask(sessionId: number): void {
    const session = this.sessions.find(s => s.id === sessionId);
    if (session) {
      session.tasks.push('');
    }
  }

  trackTask(index: number, _task: string): number {
    return index;
  }

  removeTask(sessionId: number, taskIndex: number): void {
    const session = this.sessions.find(s => s.id === sessionId);
    if (session && session.tasks.length > 1) {
      session.tasks.splice(taskIndex, 1);
    }
  }

  private validateAndPreviewImage(
    file: File,
    options: { maxWidth: number; maxHeight: number; allowLessOrEqual: boolean },
    onValid: (preview: string) => void,
    inputElement?: HTMLInputElement
  ) {
    const img = new Image();
    const reader = new FileReader();
    
    const resetInput = () => {
      if (inputElement) {
        inputElement.value = '';
      }
    };
    
    reader.onload = (e: any) => {
      const dataUrl = e.target.result as string;
      img.onload = () => {
        const { maxWidth, maxHeight, allowLessOrEqual } = options;
        const isValid = allowLessOrEqual
          ? img.width <= maxWidth && img.height <= maxHeight
          : img.width === maxWidth && img.height === maxHeight;

        if (isValid) {
          onValid(dataUrl);
        } else {
          const conditionText = allowLessOrEqual
            ? `must be less than or equal to ${maxWidth}px (width) and ${maxHeight}px (height)`
            : `must be exactly ${maxWidth}px (width) and ${maxHeight}px (height)`;
          alert(`Image is not according to the given criterion: it ${conditionText}.`);
          resetInput();
        }
      };
      img.onerror = () => {
        alert('Error loading image. Please try again.');
        resetInput();
      };
      img.src = dataUrl;
    };
    reader.onerror = () => {
      alert('Error reading image file. Please try again.');
      resetInput();
    };
    reader.readAsDataURL(file);
  }

  private validateAudio(file: File, onValid: (name: string) => void, _inputElement?: HTMLInputElement) {
    onValid(file.name);
  }

  onQuestImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAndPreviewImage(
        file,
        {
          maxWidth: this.questImageMaxWidth,
          maxHeight: this.questImageMaxHeight,
          allowLessOrEqual: true,
        },
        (preview) => {
          this.questImageFile = file;
          this.questImagePreview = preview;
        },
        event.target
      );
    }
  }

  onIntroductionImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAndPreviewImage(
        file,
        {
          maxWidth: this.introImageWidthReq,
          maxHeight: this.introImageHeightReq,
          allowLessOrEqual: false,
        },
        (preview) => {
          this.introductionImageFile = file;
          this.introductionImagePreview = preview;
        },
        event.target
      );
    }
  }

  onIntroductionAudioSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAudio(file, (name) => {
        // Revoke old blob URL if it exists
        if (this.audioBlobUrls['intro']) {
          URL.revokeObjectURL(this.audioBlobUrls['intro']);
        }
        this.introductionAudioFile = file;
        this.introductionAudioName = name;
        // Clear the old URL when new file is uploaded so preview uses new file
        this.introductionAudioUrl = null;
        // Clear the cached blob URL so a new one will be created
        delete this.audioBlobUrls['intro'];
        // Clear any existing audio element for this
        if (this.audioElements['intro']) {
          this.audioElements['intro'].pause();
          delete this.audioElements['intro'];
          delete this.audioElementUrls['intro'];
        }
      }, event.target);
    }
  }

  onSessionImageSelected(event: any, sessionId: number): void {
    const file = event.target.files[0];
    if (file) {
      const session = this.sessions.find(s => s.id === sessionId);
      if (session) {
        this.validateAndPreviewImage(
          file,
          {
            maxWidth: this.introImageWidthReq,
            maxHeight: this.introImageHeightReq,
            allowLessOrEqual: false,
          },
          (preview) => {
            session.image = preview;
            session.imageFile = file;
          },
          event.target
        );
      }
    }
  }

  onSessionAudioSelected(event: any, sessionId: number): void {
    const file = event.target.files[0];
    if (file) {
      const session = this.sessions.find(s => s.id === sessionId);
      if (session) {
        this.validateAudio(file, (name) => {
          const audioId = 'session-' + session.id;
          // Revoke old blob URL if it exists
          if (this.audioBlobUrls[audioId]) {
            URL.revokeObjectURL(this.audioBlobUrls[audioId]);
          }
          session.audio = name;
          session.audioFile = file;
          // Clear the old URL when new file is uploaded so preview uses new file
          session.audioUrl = null;
          // Clear the cached blob URL so a new one will be created
          delete this.audioBlobUrls[audioId];
          // Clear any existing audio element for this
          if (this.audioElements[audioId]) {
            this.audioElements[audioId].pause();
            delete this.audioElements[audioId];
            delete this.audioElementUrls[audioId];
          }
        }, event.target);
      }
    }
  }

  validateQuestForm(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate Dashboard Card Section
    if (!this.questName || this.questName.trim() === '') {
      errors.push('Quest Name is required');
    }
    if (!this.questDescription || this.questDescription.trim() === '') {
      errors.push('Quest Description is required');
    }
    if (!this.questImageFile && !this.questImagePreview) {
      errors.push('Quest Image is required');
    }

    // Validate Introduction
    if (!this.introductionDescription || this.introductionDescription.trim() === '') {
      errors.push('Introduction Description is required');
    }
    if (!this.introductionImageFile && !this.introductionImagePreview) {
      errors.push('Introduction Image is required');
    }
    if (!this.introductionAudioFile && !this.introductionAudioName) {
      errors.push('Introduction Audio is required');
    }

    // Validate Sessions
    if (this.sessions.length === 0) {
      errors.push('At least one Session is required');
    } else {
      this.sessions.forEach((session, idx) => {
        const sessionNum = idx + 1;
        if (!session.heading || session.heading.trim() === '') {
          errors.push(`Session ${sessionNum} Heading is required`);
        }
        if (!session.description || session.description.trim() === '') {
          errors.push(`Session ${sessionNum} Description is required`);
        }
        if (!session.imageFile && !session.image) {
          errors.push(`Session ${sessionNum} Image is required`);
        }
        if (!session.audioFile && !session.audio) {
          errors.push(`Session ${sessionNum} Audio is required`);
        }
        const validTasks = session.tasks.filter(t => t && t.trim() !== '');
        if (validTasks.length === 0) {
          errors.push(`Session ${sessionNum} must have at least one Task`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  saveQuest(): void {
    // Validate all fields
    const validation = this.validateQuestForm();
    if (!validation.isValid) {
      this.validationErrors = validation.errors;
      this.showValidationModal = true;
      return;
    }

    this.isSaving = true;
    const formData = new FormData();

    if (this.isEditMode && this.originalQuestData) {
      // EDIT MODE: Only send changed fields
      const original = this.originalQuestData;

      // Check and add changed basic fields
      if (this.questName !== (original.name || '')) {
        formData.append('QuestName', this.questName);
      }
      if (this.questDescription !== (original.shortDescription || '')) {
        formData.append('QuestDescription', this.questDescription);
      }
      if (this.introductionDescription !== (original.introduction?.description || '')) {
        formData.append('IntroductionDescription', this.introductionDescription);
      }

      // Always send quest id for clarity when updating/handling deletions
      if (this.questId) {
        formData.append('QuestId', this.questId);
      }

      // Images & audio - only send if new file was uploaded (changed)
      if (this.questImageFile) {
        formData.append('QuestImage', this.questImageFile, this.questImageFile.name);
      }
      if (this.introductionImageFile) {
        formData.append('IntroductionImages', this.introductionImageFile, this.introductionImageFile.name);
      }
      if (this.introductionAudioFile) {
        formData.append('IntroductionVoice', this.introductionAudioFile, this.introductionAudioFile.name);
      }

      const originalSessions = original.sessions || [];

      // If we are only deleting sessions, do NOT send any Sessions[*] data
      if (this.deletedSessionIds.length === 0) {
        // Sessions - check for changes
        this.sessions.forEach((s, idx) => {
          const originalSession = originalSessions[idx];
          
          // Check if session heading changed
          if (!originalSession || s.heading !== (originalSession.shortDescription || '')) {
            formData.append(`Sessions[${idx}].Heading`, s.heading);
          }
          
          // Check if session description changed
          if (!originalSession || s.description !== (originalSession.longDescription || '')) {
            formData.append(`Sessions[${idx}].Description`, s.description);
          }

          // Tasks - check if changed
          const originalTasks: string[] = [];
          if (originalSession?.tasks && Array.isArray(originalSession.tasks)) {
            originalSession.tasks.forEach((task: any) => {
              if (task.activities && Array.isArray(task.activities)) {
                originalTasks.push(...task.activities);
              } else if (typeof task === 'string') {
                originalTasks.push(task);
              }
            });
          }
          const currentTasks = s.tasks.filter(t => t && t.trim() !== '');
          const tasksChanged = JSON.stringify(currentTasks) !== JSON.stringify(originalTasks);
          
          if (tasksChanged || !originalSession) {
            currentTasks.forEach((t, ti) => {
              formData.append(`Sessions[${idx}].Tasks[${ti}]`, t);
            });
          }

          // Session images & audio - only send if new file was uploaded (changed)
          if (s.imageFile) {
            formData.append(`Session[${idx}].Images`, s.imageFile, s.imageFile.name);
          }
          if (s.audioFile) {
            formData.append(`Session[${idx}].Voice`, s.audioFile, s.audioFile.name);
          }
        });
      }

      // If any sessions were removed in edit mode, send their backend IDs
      if (this.deletedSessionIds.length > 0) {
        this.deletedSessionIds.forEach((id, idx) => {
          formData.append(`DeletedSessionIds[${idx}]`, id);
        });
      }

      console.log('Updating quest payload (FormData) - only changed fields...');
      this.questService.updateQuest(this.questId!, formData).subscribe({
        next: (res) => {
          console.log('Quest updated successfully', res);
          this.isSaving = false;
          this.showSuccessModal = true;
        },
        error: (err) => {
          console.error('Error updating quest', err);
          this.isSaving = false;
          alert('Failed to update quest. Please try again.');
        },
      });
    } else {
      // CREATE MODE: Send all fields
      formData.append('QuestName', this.questName);
      formData.append('QuestDescription', this.questDescription);
      formData.append('IntroductionDescription', this.introductionDescription);

      // Images & audio (top-level)
      if (this.questImageFile) {
        formData.append('QuestImage', this.questImageFile, this.questImageFile.name);
      }
      if (this.introductionImageFile) {
        formData.append('IntroductionImages', this.introductionImageFile, this.introductionImageFile.name);
      }
      if (this.introductionAudioFile) {
        formData.append('IntroductionVoice', this.introductionAudioFile, this.introductionAudioFile.name);
      }

      // Sessions
      this.sessions.forEach((s, idx) => {
        formData.append(`Sessions[${idx}].Heading`, s.heading);
        formData.append(`Sessions[${idx}].Description`, s.description);
        s.tasks.forEach((t, ti) => {
          if (t && t.trim() !== '') {
            formData.append(`Sessions[${idx}].Tasks[${ti}]`, t);
          }
        });
        if (s.imageFile) {
          formData.append(`Session[${idx}].Images`, s.imageFile, s.imageFile.name);
        }
        if (s.audioFile) {
          formData.append(`Session[${idx}].Voice`, s.audioFile, s.audioFile.name);
        }
      });

      console.log('Creating quest payload (FormData)...');
      this.questService.saveQuest(formData).subscribe({
      next: (res) => {
        console.log('Quest saved successfully', res);
          this.isSaving = false;
          this.showSuccessModal = true;
      },
      error: (err) => {
        console.error('Error saving quest', err);
          this.isSaving = false;
          alert('Failed to save quest. Please try again.');
      },
    });
    }
  }

  onSuccessModalOk(): void {
    this.showSuccessModal = false;
    this.resetForm();
    this.showForm = false;
    this.isEditMode = false;
    this.questId = null;
    this.originalQuestData = null;
    this.loadQuests(); // Reload quests list to show the updated/created quest
    // Emit event to parent to remove blur and enable tabs
    this.questCreated.emit();
  }

  resetForm(preserveEditMode: boolean = false): void {
    this.questName = '';
    this.questDescription = '';
    this.questImagePreview = '';
    this.questImageFile = null;
    this.introductionExpanded = false;
    this.introductionDescription = '';
    this.introductionImagePreview = '';
    this.introductionImageFile = null;
    this.introductionAudioFile = null;
    this.introductionAudioName = '';
    this.introductionAudioUrl = null;
    this.sessions = [];
    this.sessionCounter = 1;
    this.showValidationModal = false;
    this.validationErrors = [];
    this.deletedSessionIds = [];
    if (!preserveEditMode) {
      this.isEditMode = false;
      this.questId = null;
      this.originalQuestData = null;
    }
  }
}

